import { mulQ4 } from "./arith";
import { DomainError } from "./errors";
import { MAX_STOCK_QTY_Q4 } from "./stock";

// ===== Situações e Gerenciador de transições (RF-PRF-03, RF-PRF-12; 04-api §4; 05 §5) =====

/** Módulos com situações configuráveis (schema `status_definitions.module`). */
export const STATUS_MODULES = [
  "sales_orders",
  "purchase_orders",
  "service_orders",
  "production_orders",
  "quotes",
] as const;
export type StatusModule = (typeof STATUS_MODULES)[number];

export const STATUS_MODULE_LABELS: Readonly<Record<StatusModule, string>> = {
  sales_orders: "Pedidos de venda",
  purchase_orders: "Pedidos de compra",
  service_orders: "Ordens de serviço",
  production_orders: "Ordens de produção",
  quotes: "Propostas comerciais",
};

/** Semântica da situação para as regras (relatórios, cancelamento). */
export const BASE_STATES = ["open", "in_progress", "done", "canceled"] as const;
export type BaseState = (typeof BASE_STATES)[number];

export const BASE_STATE_LABELS: Readonly<Record<BaseState, string>> = {
  open: "Em aberto",
  in_progress: "Em andamento",
  done: "Concluída",
  canceled: "Cancelada",
};

/** Cor da situação = tom do StatusBadge (design §3: nunca cor literal). */
export const STATUS_TONES = ["info", "warning", "success", "danger", "secondary"] as const;
export type StatusTone = (typeof STATUS_TONES)[number];

/** Ações que uma transição executa, na transação do documento. */
export const TRANSITION_ACTIONS = [
  "reserve_stock",
  "release_stock",
  "post_stock",
  "reverse_stock",
  "post_financial",
  "reverse_financial",
] as const;
export type TransitionAction = (typeof TRANSITION_ACTIONS)[number];

export const ACTION_LABELS: Readonly<Record<TransitionAction, string>> = {
  reserve_stock: "Reservar estoque",
  release_stock: "Liberar reserva",
  post_stock: "Lançar estoque",
  reverse_stock: "Estornar estoque",
  post_financial: "Lançar contas",
  reverse_financial: "Estornar contas",
};

/**
 * Ações que fazem sentido em cada módulo. Proposta não mexe em estoque nem em contas; compra não reserva (é entrada);
 * produção não lança contas.
 */
export const MODULE_TRANSITION_ACTIONS: Readonly<Record<StatusModule, readonly TransitionAction[]>> = {
  sales_orders: TRANSITION_ACTIONS,
  service_orders: TRANSITION_ACTIONS,
  purchase_orders: ["post_stock", "reverse_stock", "post_financial", "reverse_financial"],
  /** OP (T3.2): lançar = produzir o saldo (apontamento); estornar = estornar os apontamentos. Reserva fica para depois. */
  production_orders: ["post_stock", "reverse_stock"],
  quotes: [],
};

/** Pares que se anulam: não podem estar na mesma transição. */
const OPPOSITES: readonly (readonly [TransitionAction, TransitionAction])[] = [
  ["reserve_stock", "release_stock"],
  ["post_stock", "reverse_stock"],
  ["post_financial", "reverse_financial"],
];

/** Tetos por empresa e módulo (as listas saem inteiras na tela). */
export const MAX_STATUSES_PER_MODULE = 50;
export const MAX_TRANSITIONS_PER_MODULE = 500;

const isAction = (a: string): a is TransitionAction => (TRANSITION_ACTIONS as readonly string[]).includes(a);

/**
 * Problema da lista de ações de uma transição, ou null. `invalid_actions`: ação desconhecida, repetida ou par oposto;
 * `action_not_allowed`: ação que não se aplica ao módulo (só confere com `module`).
 */
export function actionsProblem(
  module: StatusModule | null,
  actions: readonly string[],
): { code: "invalid_actions" | "action_not_allowed"; message: string } | null {
  const seen = new Set<string>();
  for (const a of actions) {
    if (!isAction(a)) return { code: "invalid_actions", message: `Ação inválida: ${a}` };
    if (seen.has(a)) return { code: "invalid_actions", message: `Ação repetida: ${ACTION_LABELS[a]}` };
    seen.add(a);
  }
  for (const [x, y] of OPPOSITES)
    if (seen.has(x) && seen.has(y))
      return {
        code: "invalid_actions",
        message: `"${ACTION_LABELS[x]}" e "${ACTION_LABELS[y]}" não podem estar na mesma transição`,
      };
  if (module) {
    const allowed = MODULE_TRANSITION_ACTIONS[module];
    const bad = [...seen].find((a) => !allowed.includes(a as TransitionAction));
    if (bad)
      return {
        code: "action_not_allowed",
        message: `"${ACTION_LABELS[bad as TransitionAction]}" não se aplica a ${STATUS_MODULE_LABELS[module].toLowerCase()}`,
      };
  }
  return null;
}

const FORWARD: ReadonlySet<TransitionAction> = new Set(["reserve_stock", "post_stock", "post_financial"]);

/**
 * `actionsProblem` + regra do destino: transição para situação cancelada só estorna (reservar ou lançar ali seria
 * desfeito na mesma hora).
 */
export function transitionActionsProblem(
  module: StatusModule,
  actions: readonly string[],
  targetBase: BaseState,
): { code: "invalid_actions" | "action_not_allowed"; message: string } | null {
  const p = actionsProblem(module, actions);
  if (p) return p;
  if (targetBase === "canceled" && actions.some((a) => FORWARD.has(a as TransitionAction)))
    return {
      code: "invalid_actions",
      message: "Transição para uma situação cancelada só estorna: tire as ações de reservar e lançar",
    };
  return null;
}

/** Ações na ordem canônica do catálogo, sem repetição (o que é gravado e exibido). */
export function normalizeActions(actions: readonly TransitionAction[]): TransitionAction[] {
  return TRANSITION_ACTIONS.filter((a) => actions.includes(a));
}

/** Código da situação a partir do rótulo: minúsculas, sem acento, `_` no lugar do resto (até 40). */
export function statusCode(label: string): string {
  const code = label
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40)
    .replace(/_+$/, "");
  return code || "situacao";
}

// ---------- situações e transições de sistema ----------

export type SystemStatus = { code: string; label: string; tone: StatusTone; baseState: BaseState };
export type SystemTransition = { from: string; to: string; actions: TransitionAction[] };

const st = (code: string, label: string, tone: StatusTone, baseState: BaseState): SystemStatus => ({
  code,
  label,
  tone,
  baseState,
});

/** Situações padrão (PRD RF-VEN-02, RF-VEN-01, RF-SUP-03, RF-SER-03; 04-api §4), na ordem de exibição. */
export const SYSTEM_STATUSES: Readonly<Record<StatusModule, readonly SystemStatus[]>> = {
  sales_orders: [
    st("em_aberto", "Em aberto", "info", "open"),
    st("em_digitacao", "Em digitação", "info", "open"),
    st("em_andamento", "Em andamento", "warning", "in_progress"),
    st("verificado", "Verificado", "warning", "in_progress"),
    st("checkout_parcial", "Checkout parcial", "warning", "in_progress"),
    st("atendido", "Atendido", "success", "done"),
    st("atendido_parcialmente", "Atendido parcialmente", "success", "in_progress"),
    st("faturado_parcialmente", "Faturado parcialmente", "success", "in_progress"),
    st("venda_agenciada", "Venda agenciada", "secondary", "done"),
    st("cancelado", "Cancelado", "danger", "canceled"),
  ],
  purchase_orders: [
    st("em_aberto", "Em aberto", "info", "open"),
    st("em_andamento", "Em andamento", "warning", "in_progress"),
    st("atendido", "Atendido", "success", "done"),
    st("cancelado", "Cancelado", "danger", "canceled"),
  ],
  service_orders: [
    st("em_aberto", "Em aberto", "info", "open"),
    st("orcada", "Orçada", "info", "open"),
    st("aprovada", "Aprovada", "warning", "in_progress"),
    st("nao_aprovada", "Não aprovada", "danger", "canceled"),
    st("em_andamento", "Em andamento", "warning", "in_progress"),
    st("aguardando_componentes", "Aguardando componentes", "warning", "in_progress"),
    st("servico_concluido", "Serviço concluído", "success", "in_progress"),
    st("finalizada", "Finalizada", "success", "done"),
    st("finalizada_sem_conserto", "Finalizada sem conserto", "secondary", "done"),
  ],
  production_orders: [
    st("em_aberto", "Em aberto", "info", "open"),
    st("em_andamento", "Em andamento", "warning", "in_progress"),
    st("finalizada", "Finalizada", "success", "done"),
    st("cancelada", "Cancelada", "danger", "canceled"),
  ],
  quotes: [
    st("em_aberto", "Em aberto", "info", "open"),
    st("enviada", "Enviada", "warning", "in_progress"),
    st("aprovada", "Aprovada", "success", "done"),
    st("nao_aprovada", "Não aprovada", "danger", "canceled"),
    st("convertida", "Convertida", "success", "done"),
  ],
};

const tr = (from: string, to: string, ...actions: TransitionAction[]): SystemTransition => ({
  from,
  to,
  actions,
});
const toCancel = (to: string, actions: TransitionAction[], ...from: string[]) =>
  from.map((f) => tr(f, to, ...actions));

/**
 * Grafo padrão (04-api §4). Reserva ao entrar em "em andamento" e baixa ao "atendido" (PRD RF-VEN-02); voltar desfaz
 * (estorno, nunca exclusão); cancelar estorna tudo.
 */
export const SYSTEM_TRANSITIONS: Readonly<Record<StatusModule, readonly SystemTransition[]>> = {
  sales_orders: [
    tr("em_digitacao", "em_aberto"),
    tr("em_aberto", "em_digitacao"),
    tr("em_aberto", "em_andamento", "reserve_stock"),
    tr("em_andamento", "em_aberto", "release_stock"),
    tr("em_andamento", "verificado"),
    tr("verificado", "em_andamento"),
    tr("verificado", "checkout_parcial"),
    tr("checkout_parcial", "verificado"),
    // Checkout (T1.12): salvar parcial a partir da fila e voltar para a separação.
    tr("em_andamento", "checkout_parcial"),
    tr("checkout_parcial", "em_andamento"),
    tr("em_aberto", "atendido", "post_stock", "post_financial"),
    tr("em_andamento", "atendido", "post_stock", "post_financial"),
    tr("verificado", "atendido", "post_stock", "post_financial"),
    tr("atendido", "em_andamento", "reserve_stock", "reverse_stock", "reverse_financial"),
    ...toCancel(
      "cancelado",
      ["reverse_stock", "reverse_financial"],
      "em_digitacao",
      "em_aberto",
      "em_andamento",
      "verificado",
      "checkout_parcial",
      "atendido",
    ),
    tr("cancelado", "em_aberto"),
  ],
  purchase_orders: [
    tr("em_aberto", "em_andamento"),
    tr("em_andamento", "em_aberto"),
    tr("em_aberto", "atendido", "post_stock", "post_financial"),
    tr("em_andamento", "atendido", "post_stock", "post_financial"),
    tr("atendido", "em_andamento", "reverse_stock", "reverse_financial"),
    ...toCancel("cancelado", ["reverse_stock", "reverse_financial"], "em_aberto", "em_andamento", "atendido"),
    tr("cancelado", "em_aberto"),
  ],
  // OS (T2.9): sem reserva (os técnicos incluem peças durante o serviço); "Finalizada" baixa as peças e lança as
  // contas a partir de qualquer etapa de trabalho; voltar de "Finalizada" estorna; "Não aprovada" é o cancelamento.
  service_orders: [
    tr("em_aberto", "orcada"),
    tr("orcada", "em_aberto"),
    tr("em_aberto", "aprovada"),
    tr("orcada", "aprovada"),
    tr("orcada", "nao_aprovada"),
    tr("em_aberto", "nao_aprovada"),
    tr("nao_aprovada", "orcada"),
    tr("nao_aprovada", "em_aberto"),
    tr("em_aberto", "em_andamento"),
    tr("aprovada", "em_andamento"),
    tr("em_andamento", "aguardando_componentes"),
    tr("aguardando_componentes", "em_andamento"),
    tr("em_andamento", "servico_concluido"),
    tr("servico_concluido", "em_andamento"),
    tr("em_aberto", "finalizada", "post_stock", "post_financial"),
    tr("aprovada", "finalizada", "post_stock", "post_financial"),
    tr("em_andamento", "finalizada", "post_stock", "post_financial"),
    tr("servico_concluido", "finalizada", "post_stock", "post_financial"),
    tr("em_andamento", "finalizada_sem_conserto", "release_stock"),
    tr("servico_concluido", "finalizada_sem_conserto", "release_stock"),
    tr("aguardando_componentes", "finalizada_sem_conserto", "release_stock"),
    tr("finalizada", "servico_concluido", "reverse_stock", "reverse_financial"),
    tr("finalizada_sem_conserto", "em_andamento"),
  ],
  // OP (T3.2, como a compra): apontar produção leva a "Em andamento"; "Finalizada" produz o saldo; voltar de
  // "Finalizada" e cancelar estornam os apontamentos.
  production_orders: [
    tr("em_aberto", "em_andamento"),
    tr("em_andamento", "em_aberto"),
    tr("em_aberto", "finalizada", "post_stock"),
    tr("em_andamento", "finalizada", "post_stock"),
    tr("finalizada", "em_andamento", "reverse_stock"),
    ...toCancel("cancelada", ["reverse_stock"], "em_aberto", "em_andamento", "finalizada"),
    tr("cancelada", "em_aberto"),
  ],
  quotes: [
    tr("em_aberto", "enviada"),
    tr("enviada", "em_aberto"),
    tr("em_aberto", "aprovada"),
    tr("enviada", "aprovada"),
    tr("enviada", "nao_aprovada"),
    tr("nao_aprovada", "em_aberto"),
  ],
};

/**
 * Situações de sistema em que só o próprio sistema entra: "Convertida" da proposta é exclusiva da conversão em pedido
 * (`convertQuote`). O motor recusa a transição para elas e o Gerenciador não cria nem restaura transição que chegue
 * nelas (`invalid_transition`).
 */
export const SYSTEM_ONLY_STATUSES: Readonly<Record<StatusModule, readonly string[]>> = {
  sales_orders: [],
  purchase_orders: [],
  service_orders: [],
  production_orders: [],
  quotes: ["convertida"],
};

export const isSystemOnlyStatus = (module: StatusModule, code: string): boolean =>
  SYSTEM_ONLY_STATUSES[module].includes(code);

// ---------- motor: plano das etapas ----------

export type StockEffect = "none" | "reserved" | "posted";
export type FinancialEffect = "none" | "posted";
export type EffectState = { stock: StockEffect; financial: FinancialEffect };

/**
 * Etapa concreta, já com a pré-condição garantida pelo plano: `reverse_stock` só com estoque lançado, `release_stock`
 * só com reserva, `post_stock` com nada ou reserva (o motor consome a reserva), `reverse_financial` só com contas.
 */
export type EffectStep = TransitionAction;

/**
 * Plano da transição (05 §5): dirigido pelo estado dos efeitos, então repetir a transição não repete nada
 * (idempotente) e voltar desfaz. Ordem: estornos antes dos lançamentos. Destino com estado base `canceled` estorna tudo
 * (04-api §4), mesmo sem as ações listadas, e ignora reservar/lançar. Reservar junto com lançar = lançar direto.
 */
export function planEffects(
  state: EffectState,
  actions: readonly TransitionAction[],
  targetBase: BaseState,
): { steps: EffectStep[]; next: EffectState } {
  // Cancelar só desfaz: nada se reserva nem se lança na entrada de uma situação cancelada.
  const want = new Set<TransitionAction>(
    targetBase === "canceled" ? ["reverse_stock", "reverse_financial"] : actions,
  );
  const steps: EffectStep[] = [];
  let { stock, financial } = state;
  if (want.has("reverse_financial") && financial === "posted") {
    steps.push("reverse_financial");
    financial = "none";
  }
  if (want.has("reverse_stock") && stock === "posted") {
    steps.push("reverse_stock");
    stock = "none";
  }
  // Estornar e reservar de novo (atendido → em andamento): a reserva aberta já é o destino, fica.
  if (
    (want.has("reverse_stock") || want.has("release_stock")) &&
    stock === "reserved" &&
    !want.has("reserve_stock")
  ) {
    steps.push("release_stock");
    stock = "none";
  }
  if (want.has("reserve_stock") && !want.has("post_stock") && stock === "none") {
    steps.push("reserve_stock");
    stock = "reserved";
  }
  if (want.has("post_stock") && stock !== "posted") {
    steps.push("post_stock");
    stock = "posted";
  }
  if (want.has("post_financial") && financial === "none") {
    steps.push("post_financial");
    financial = "posted";
  }
  return { steps, next: { stock, financial } };
}

// ---------- linhas de estoque do documento ----------

/**
 * Teto de linhas de estoque de um documento depois de expandir os kits (o motor recusa acima: `invalid_qty`). Limita o
 * trabalho de uma transição (uma reserva ou movimento por linha somada, tudo numa transação do DO).
 */
export const MAX_DOC_STOCK_LINES = 2_000;
/** Histórico de situações de um documento: só as 500 mudanças mais recentes (a resposta não cresce sem limite). */
export const MAX_STATUS_HISTORY = 500;

export type StockDirection = "out" | "in";
export type DocStockLine = {
  productId: string;
  /** null = depósito padrão da empresa. */
  warehouseId: string | null;
  qtyQ4: number;
  direction: StockDirection;
  /** Custo unitário da entrada (compra, produção). Saída usa o custo médio vigente. */
  unitCostCents?: number | null;
};

function assertQty(q: number) {
  if (!Number.isSafeInteger(q) || q <= 0)
    throw new DomainError("invalid_qty", `Quantidade do item deve ser positiva: ${q}`);
}

/**
 * Soma as linhas por produto × depósito × direção, na ordem da primeira ocorrência (o motor reserva e baixa uma vez por
 * par). Soma em BigInt; acima de MAX_STOCK_QTY_Q4, `invalid_qty`. Custo da entrada agregada = média ponderada pela
 * quantidade das linhas com custo (arredondamento meio para cima).
 */
export function aggregateStockLines(lines: readonly DocStockLine[]): DocStockLine[] {
  const acc = new Map<string, { line: DocStockLine; qty: bigint; cost: bigint; costed: bigint }>();
  for (const l of lines) {
    assertQty(l.qtyQ4);
    const key = `${l.productId}|${l.warehouseId ?? ""}|${l.direction}`;
    const cur = acc.get(key) ?? { line: l, qty: 0n, cost: 0n, costed: 0n };
    cur.qty += BigInt(l.qtyQ4);
    if (l.unitCostCents !== undefined && l.unitCostCents !== null) {
      cur.cost += BigInt(l.unitCostCents) * BigInt(l.qtyQ4);
      cur.costed += BigInt(l.qtyQ4);
    }
    acc.set(key, cur);
  }
  return [...acc.values()].map(({ line, qty, cost, costed }) => {
    if (qty > BigInt(MAX_STOCK_QTY_Q4))
      throw new DomainError("invalid_qty", "Quantidade somada do item acima do limite");
    const out: DocStockLine = {
      productId: line.productId,
      warehouseId: line.warehouseId,
      qtyQ4: Number(qty),
      direction: line.direction,
    };
    if (costed > 0n) out.unitCostCents = Number((2n * cost + costed) / (2n * costed));
    return out;
  });
}

/**
 * Linha de kit (formato E, sem saldo próprio) vira os componentes: quantidade da linha × quantidade do componente
 * (q4 × q4, arredondamento comercial). Componente que daria zero ou estoura o produto: `invalid_qty` (nunca some calado
 * do estoque). A entrada do kit não leva custo aos componentes (usam o custo do cadastro).
 */
export function kitComponentLines(
  line: DocStockLine,
  components: readonly { componentId: string; qtyQ4: number }[],
): DocStockLine[] {
  assertQty(line.qtyQ4);
  const out: DocStockLine[] = [];
  for (const c of components) {
    let qtyQ4: number;
    try {
      qtyQ4 = mulQ4(c.qtyQ4, line.qtyQ4);
    } catch {
      throw new DomainError("invalid_qty", "Quantidade do componente do kit acima do limite");
    }
    if (qtyQ4 <= 0)
      throw new DomainError(
        "invalid_qty",
        "Quantidade do kit pequena demais: um componente ficaria com quantidade zero",
      );
    out.push({ productId: c.componentId, warehouseId: line.warehouseId, qtyQ4, direction: line.direction });
  }
  return out;
}
